import React, { useState } from 'react';
import './RegLookup.scss';
import ReactGA from 'react-ga4';

// ── Map DVLA fuelType strings → internal codes + colours
const DVLA_FUEL_MAP = {
  'PETROL':           { id: 'E10',      label: 'Petrol (E10)',       color: '#e8ff47' },
  'DIESEL':           { id: 'B7',       label: 'Diesel (B7)',        color: '#60a5fa' },
  'ELECTRIC':         { id: 'Electric', label: 'Electric (EV)',      color: '#a78bfa' },
  'HYBRID ELECTRIC':  { id: 'Hybrid',   label: 'Hybrid Electric',    color: '#4ade80' },
  'PETROL/ELECTRIC':  { id: 'Hybrid',   label: 'Petrol Hybrid',      color: '#4ade80' },
  'DIESEL/ELECTRIC':  { id: 'Hybrid',   label: 'Diesel Hybrid',      color: '#4ade80' },
  'GAS':              { id: 'Gas',      label: 'LPG / Gas',          color: '#fb923c' },
  'GAS/PETROL':       { id: 'Gas',      label: 'LPG / Petrol',       color: '#fb923c' },
  'FUEL CELLS':       { id: 'Hydrogen', label: 'Hydrogen Fuel Cell', color: '#38bdf8' },
  'OTHER':            { id: 'Other',    label: 'Other Fuel Type',    color: '#9ca3af' },
};

// ── Backend URL — points to the Node server that proxies DVLA
// In development: http://localhost:3001
// In production: set REACT_APP_API_URL to your deployed server URL
// In dev, CRA proxies /api/* to localhost:3001 (set in package.json).
// In production, set REACT_APP_API_URL to your deployed backend URL.
const API_BASE = process.env.REACT_APP_API_URL || '';

function VehicleDataGrid({ vehicle }) {
  const fields = [
    { label: 'Make',        value: vehicle.make },
    { label: 'Year',        value: vehicle.yearOfManufacture },
    { label: 'Colour',      value: vehicle.colour },
    { label: 'Engine',      value: vehicle.engineCapacity ? `${vehicle.engineCapacity}cc` : null },
    { label: 'CO₂',        value: vehicle.co2Emissions ? `${vehicle.co2Emissions}g/km` : null },
    { label: 'Euro Status', value: vehicle.euroStatus },
    // { label: 'Tax Status',  value: vehicle.taxStatus },
    // { label: 'MOT Status',  value: vehicle.motStatus },
    // { label: 'MOT Expiry',  value: vehicle.motExpiryDate },
    // { label: 'Last V5C',    value: vehicle.dateOfLastV5CIssued },
  ].filter((f) => f.value);

  return (
    <div className="vehicle-data-grid">
      {fields.map(({ label, value }) => (
        <div key={label} className="vehicle-data-grid__item">
          <span className="vehicle-data-grid__label">{label}</span>
          <span className="vehicle-data-grid__value">{String(value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function RegLookup() {
  const [reg,     setReg]     = useState('');
  const [step,    setStep]    = useState('idle'); // idle | dvla | claude | done | error
  const [dvlaData, setDvla]   = useState(null);
  const [advice,  setAdvice]  = useState(null);
  const [errorMsg, setError]  = useState('');

  const handleLookup = async () => {
    const clean = reg.trim().toUpperCase().replace(/\s+/g, '');
    if (!clean) return;

    setStep('dvla');
    setDvla(null);
    setAdvice(null);
    setError('');

    // ── STEP 1: Call our Node backend which proxies to DVLA
    // The DVLA API key lives server-side only — never in the browser.
    let vehicle;
    try {
      const res = await fetch(`${API_BASE}/api/vehicle`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ registrationNumber: clean }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || `Server error (${res.status})`);
      }

      vehicle = data;
      setDvla(vehicle);
      ReactGA.event({                                        
        category: 'RegLookup',
        action: 'DVLA Lookup Success',
        label: vehicle.fuelType || 'Unknown',
      });
    } catch (err) {
      setStep('error');
      ReactGA.event({ category: 'RegLookup', action: 'DVLA Lookup Error' });
      // Give a helpful message if the backend isn't running
      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        setError(
          'Cannot connect to the TTT backend server. ' +
          'Make sure the server is running on port 3001 (see README).'
        );
      } else {
        setError(err.message || 'Could not look up this vehicle. Please try again.');
      }
      return;
    }

    // ── STEP 2: Claude AI enrichment using the confirmed DVLA data
    setStep('claude');
    const dvlaFuelKey = (vehicle.fuelType || '').toUpperCase();
    const mappedFuel  = DVLA_FUEL_MAP[dvlaFuelKey] || {
      id:    dvlaFuelKey,
      label: vehicle.fuelType || 'Unknown',
      color: '#9ca3af',
    };

    try {
      const claudeRes = await fetch('https://api.anthropic.com/v1/messages', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model:      'claude-sonnet-4-20250514',
          max_tokens: 800,
          messages: [{
            role:    'user',
            content: `The DVLA Vehicle Enquiry Service has returned confirmed data for UK registration ${clean}:

Make: ${vehicle.make || 'Unknown'}
Year of manufacture: ${vehicle.yearOfManufacture || 'Unknown'}
Engine capacity: ${vehicle.engineCapacity ? vehicle.engineCapacity + 'cc' : 'Unknown'}
Fuel type (DVLA confirmed): ${vehicle.fuelType || 'Unknown'}
CO2 emissions: ${vehicle.co2Emissions ? vehicle.co2Emissions + 'g/km' : 'Unknown'}
Euro status: ${vehicle.euroStatus || 'Unknown'}
Colour: ${vehicle.colour || 'Unknown'}

Provide a helpful, practical fuel recommendation for a UK driver. Return ONLY a valid JSON object — no markdown, no backticks, no extra text:
{
  "headline": "One clear sentence about the correct fuel grade for this specific vehicle",
  "grade": "Specific recommended grade: E10 Petrol | E5 Super Unleaded | B7 Diesel | Super Diesel | Electric",
  "detail": "2-3 sentences explaining why this grade suits this exact car, any performance benefits, and efficiency tips for this engine",
  "tip": "One specific money-saving or efficiency tip tailored to this fuel type and engine size",
  "compatibilityNote": "Important note if applicable — E10 compatibility for older cars, AdBlue requirement for Euro 6 diesels, EV range notes, etc. Set to null if nothing notable",
  "alternativeGrade": "Optional better-quality alternative e.g. E5 for petrol, Super Diesel for diesel. Set to null if not applicable"
}`,
          }],
        }),
      });

      const claudeData = await claudeRes.json();
      const raw = claudeData.content?.find((b) => b.type === 'text')?.text || '';

      let parsed;
      try {
        parsed = JSON.parse(raw.replace(/```json|```/g, '').trim());
      } catch {
        parsed = {
          headline: `Your ${vehicle.make || 'vehicle'} (${vehicle.yearOfManufacture || ''}) uses ${vehicle.fuelType || 'standard'} fuel.`,
          grade: mappedFuel.label,
          detail: raw || 'Fuel type confirmed by the DVLA Vehicle Enquiry Service.',
          tip: 'Fill up mid-week — prices are typically 0.5–1p lower than at weekends.',
          compatibilityNote: null,
          alternativeGrade: null,
        };
      }

      setAdvice({ ...parsed, mappedFuel });
      setStep('done');
      ReactGA.event({                                        // 👈
        category: 'RegLookup',
        action: 'Fuel Advice Generated',
        label: parsed.grade,
      });

    } catch {
      // Claude failed but we still have good DVLA data — show a basic result
      setAdvice({
        headline: `Your ${vehicle.make || 'vehicle'} (${vehicle.yearOfManufacture || ''}) requires ${vehicle.fuelType || 'standard'} fuel.`,
        grade: mappedFuel.label,
        detail: 'Fuel type confirmed by the official DVLA Vehicle Enquiry Service.',
        tip: 'Supermarkets are typically 4–6p cheaper per litre than motorway forecourts.',
        compatibilityNote: null,
        alternativeGrade: null,
        mappedFuel,
      });
      setStep('done');
    }
  };

  const isLoading = step === 'dvla' || step === 'claude';
  const stepLabel = step === 'dvla'
    ? 'Querying DVLA Vehicle Register…'
    : 'Generating fuel recommendation…';

  return (
    <section id="reg" className="reg-lookup">
      <div className="reg-lookup__inner">

        <div className="reg-lookup__header">
          <p className="reg-lookup__label">DVLA Official Data</p>
          <h2 className="reg-lookup__title">What Fuel Does My Car Need?</h2>
          <p className="reg-lookup__desc">
            Enter your UK number plate. We query the official{' '}
            <strong>DVLA Vehicle Enquiry Service</strong> for your car's confirmed fuel type,
            then give you specific grade recommendations and money-saving tips.
          </p>
        </div>

        {/* Plate input */}
        <div className="reg-lookup__input-row">
          <div className="reg-lookup__plate-wrap">
            <span className="reg-lookup__plate-flag">🇬🇧</span>
            <input
              className="reg-lookup__plate-input"
              type="text"
              placeholder="AB12 CDE"
              value={reg}
              onChange={(e) => setReg(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && !isLoading && handleLookup()}
              maxLength={8}
              spellCheck={false}
            />
          </div>
          <button
            className="reg-lookup__btn"
            onClick={handleLookup}
            disabled={isLoading || !reg.trim()}
          >
            {isLoading ? <span className="reg-lookup__spinner" /> : 'Check Fuel →'}
          </button>
        </div>

        {/* Loading with step label */}
        {isLoading && (
          <div className="reg-lookup__loading">
            <span className="reg-lookup__spinner reg-lookup__spinner--large" />
            {stepLabel}
          </div>
        )}

        {/* Error */}
        {step === 'error' && (
          <div className="reg-lookup__error">
            <span>⚠️</span>
            <div>
              <strong>{errorMsg}</strong>
              {errorMsg.includes('backend server') && (
                <p style={{ marginTop: '0.4rem', fontSize: '0.8rem', opacity: 0.75 }}>
                  Run <code>cd ttt-fuel-server && npm start</code> in a separate terminal.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Results */}
        {step === 'done' && dvlaData && advice && (
          <div
            className="reg-lookup__result"
            style={{ '--fuel-color': advice.mappedFuel?.color || '#e8ff47' }}
          >
            <div className="reg-result__source">
              <span className="reg-result__source-badge">✓ DVLA Verified</span>
              <span className="reg-result__source-text">
                Data from the official UK Vehicle Register
                {dvlaData._cached && ' · (cached)'}
              </span>
            </div>

            <div className="reg-result__top">
              <div className="reg-result__badge">{advice.grade}</div>
              <div className="reg-result__vehicle">
                {dvlaData.make}
                {dvlaData.yearOfManufacture ? ` · ${dvlaData.yearOfManufacture}` : ''}
                {dvlaData.colour ? ` · ${dvlaData.colour}` : ''}
              </div>
            </div>

            <h3 className="reg-result__headline">{advice.headline}</h3>
            <p className="reg-result__detail">{advice.detail}</p>

            <VehicleDataGrid vehicle={dvlaData} />

            {advice.alternativeGrade && (
              <div className="reg-result__alt">
                <span className="reg-result__alt-label">Premium Alternative</span>
                {advice.alternativeGrade}
              </div>
            )}

            {advice.tip && (
              <div className="reg-result__tip">
                <span>💡</span> {advice.tip}
              </div>
            )}

            {advice.compatibilityNote && (
              <div className="reg-result__warning">
                <span>⚠️</span> {advice.compatibilityNote}
              </div>
            )}

            <p className="reg-result__disclaimer">
              Fuel type confirmed by DVLA Vehicle Enquiry Service. Always verify on your
              fuel filler cap or vehicle handbook before fuelling. Misfuelling can cause
              serious engine damage.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
